import React from "react";
import { CheckCircle, Circle, ArrowRight } from "lucide-react";
import { ProjectSetupPhase, ProjectWorkflowStep } from "@/types";
import { cn } from "@/lib/utils";

interface ProjectWorkflowStepperProps {
  currentPhase: ProjectSetupPhase;
  hasDocuments: boolean;
  hasContract: boolean;
  contractExtracted: boolean;
  documentsConfirmed?: boolean;
}

export default function ProjectWorkflowStepper({
  currentPhase,
  hasDocuments,
  contractExtracted,
  documentsConfirmed = false,
}: ProjectWorkflowStepperProps) {
  
  // Define the order of project setup phases
  const phaseOrder: ProjectSetupPhase[] = ['setup', 'documents', 'contract-extracted', 'active'];
  
  // Find the index of the current phase
  const currentPhaseIndex = phaseOrder.indexOf(currentPhase);
  
  // Function to determine step status (complete/current/pending)
  const getStepStatus = (stepPhase: ProjectSetupPhase): "complete" | "current" | "pending" => {
    const stepIndex = phaseOrder.indexOf(stepPhase);
    const currentIndex = currentPhaseIndex;
    
    if (stepIndex < currentIndex) return "complete"; // Previous phases
    if (stepIndex === currentIndex) return "current"; // Current phase
    return "pending"; // Future phases
  };
  
  // Function to determine if a step is completed based on actual completion criteria
  const isStepCompleted = (stepPhase: ProjectSetupPhase): boolean => {
    switch (stepPhase) {
      case 'setup':
        return true; // Setup is always complete if we're viewing the project
      case 'documents':
        return hasDocuments && documentsConfirmed;
      case 'contract-extracted':
        return contractExtracted;
      case 'active':
        return contractExtracted && currentPhase === 'active';
      default:
        return false;
    }
  };

  const steps: ProjectWorkflowStep[] = [
    {
      phase: 'setup',
      label: 'Project Setup',
      description: 'Basic project info',
      completed: isStepCompleted('setup'),
      current: getStepStatus('setup') === 'current',
    },
    {
      phase: 'documents',
      label: 'Upload Documents',
      description: 'Contract & documents',
      completed: isStepCompleted('documents'),
      current: getStepStatus('documents') === 'current',
    },
    {
      phase: 'contract-extracted',
      label: 'Populate Details',
      description: 'Extract and confirm project details',
      completed: isStepCompleted('contract-extracted'),
      current: getStepStatus('contract-extracted') === 'current',
    },
    {
      phase: 'active',
      label: 'Project Active',
      description: 'Ready for claims',
      completed: isStepCompleted('active'),
      current: getStepStatus('active') === 'current',
    },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <h3 className="text-base font-medium text-gray-900 mb-3">Project Setup Progress</h3>
      
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <React.Fragment key={step.phase}>
            <div className="flex flex-col items-center flex-1">
              <div className={cn(
                "flex items-center justify-center w-8 h-8 rounded-full border-2",
                step.completed
                  ? "bg-green-100 border-green-500 text-green-700"
                  : step.current
                  ? "bg-blue-100 border-blue-500 text-blue-700"
                  : "bg-gray-100 border-gray-300 text-gray-500"
              )}>
                {step.completed ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <Circle className="w-5 h-5" />
                )}
              </div>
              
              <div className="mt-1.5 text-center">
                <div className={cn(
                  "text-xs font-medium leading-tight",
                  step.completed
                    ? "text-green-700"
                    : step.current
                    ? "text-blue-700"
                    : "text-gray-500"
                )}>
                  {step.label}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 leading-tight">
                  {step.description}
                </div>
              </div>
            </div>
            
            {index < steps.length - 1 && (
              <div className="flex items-center px-2">
                <ArrowRight className={cn(
                  "w-4 h-4",
                  step.completed ? "text-green-500" : "text-gray-300"
                )} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
} 